import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Plane, Ship, Share2, Printer, Search, Sparkles } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Timeline } from "@/components/Timeline";
import { toast } from "sonner";

const Row = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-right text-sm font-semibold text-slate-800" data-testid={testId}>
      {value || "—"}
    </span>
  </div>
);

export default function TrackingResult() {
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [summary, setSummary] = useState("");

  useEffect(() => {
    setSummary("");
    if (!data) return;
    api
      .get(`/track/${encodeURIComponent(trackingNumber)}/summary`)
      .then(({ data }) => setSummary(data.summary))
      .catch(() => setSummary(""));
  }, [data, trackingNumber]);

  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get(`/track/${encodeURIComponent(trackingNumber)}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        setData(null);
        setError(formatApiError(e.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  }, [trackingNumber]);

  return (
    <div className="min-h-[70vh] bg-[#F8FAFC] px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <Button
          variant="ghost"
          className="mb-6 gap-2 pl-0 text-sm text-slate-600"
          onClick={() => navigate("/")}
          data-testid="back-home-button"
        >
          <ArrowLeft className="h-4 w-4" /> Nouvelle recherche
        </Button>

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500"
               data-testid="tracking-loading">
            Chargement du suivi…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8" data-testid="tracking-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-[#DC2626]" />
              <div>
                <h1 className="text-xl font-bold text-[#DC2626]">Colis introuvable</h1>
                <p className="mt-1 text-sm text-rose-800">{error}</p>
                <p className="mt-1 font-mono text-xs text-rose-600">Recherché : {trackingNumber}</p>
              </div>
            </div>
            <form
              className="mt-6 flex flex-col gap-3 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (query.trim()) navigate(`/suivi/${encodeURIComponent(query.trim().toUpperCase())}`);
              }}
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="HTL-2026-XXXXX"
                  className="h-12 bg-white pl-9 font-mono uppercase"
                  data-testid="retry-tracking-input"
                />
              </div>
              <Button type="submit" className="h-12 bg-[#1E3A8A] px-6 font-semibold hover:bg-[#172d6d]"
                      data-testid="retry-tracking-submit">
                Réessayer
              </Button>
            </form>
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6" data-testid="tracking-result">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    Numéro de suivi
                  </p>
                  <h1 className="mt-1 font-mono text-2xl font-extrabold tracking-wider text-[#1E3A8A] sm:text-3xl"
                      data-testid="result-tracking-number">
                    {data.tracking_number}
                  </h1>
                  <div className="mt-3">
                    <StatusBadge status={data.status} testId="result-status-badge" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 border-slate-300"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                        toast.success("Lien de suivi copié");
                      } catch {
                        toast.error("Copie impossible. Sélectionnez le lien manuellement.");
                      }
                    }}
                    data-testid="share-tracking-button"
                  >
                    <Share2 className="h-4 w-4" /> Partager
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2 border-slate-300"
                          onClick={() => window.print()} data-testid="print-tracking-button">
                    <Printer className="h-4 w-4" /> Imprimer
                  </Button>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: "Type d'envoi", value: data.shipping_type === "aerien" ? "Aérien" : "Maritime",
                    icon: data.shipping_type === "aerien" ? Plane : Ship, testId: "result-shipping-type" },
                  { label: "Poids", value: `${data.weight_kg} kg`, testId: "result-weight" },
                  { label: "Destination", value: data.destination_city, testId: "result-destination" },
                  { label: "Livraison estimée", value: formatDate(data.estimated_delivery),
                    testId: "result-eta" },
                ].map((k) => (
                  <div key={k.label} className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{k.label}</p>
                    <p className="mt-1 flex items-center gap-2 text-base font-bold text-slate-900"
                       data-testid={k.testId}>
                      {k.icon && <k.icon className="h-4 w-4 text-[#2563EB]" />}
                      {k.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-3">
                {summary && (
                  <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50/70 p-4"
                       data-testid="ai-summary-card">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1E3A8A]">
                      <Sparkles className="h-3.5 w-3.5" /> Résumé de votre suivi
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700" data-testid="ai-summary-text">
                      {summary}
                    </p>
                  </div>
                )}
                <h2 className="text-lg font-semibold text-slate-900">Historique du trajet</h2>
                <div className="mt-6">
                  <Timeline history={data.history} />
                </div>
              </div>

              <div className="space-y-6 lg:col-span-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900">Expéditeur</h3>
                  <div className="mt-3">
                    <Row label="Nom" value={data.sender.name} testId="result-sender-name" />
                    <Row label="Téléphone" value={data.sender.phone} />
                    <Row label="Zone" value={data.sender.address} />
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900">Destinataire</h3>
                  <div className="mt-3">
                    <Row label="Nom" value={data.recipient.name} testId="result-recipient-name" />
                    <Row label="Téléphone" value={data.recipient.phone} />
                    <Row label="Zone" value={data.recipient.address} />
                  </div>
                  <p className="mt-4 font-mono text-[10px] uppercase leading-relaxed tracking-wider text-slate-400">
                    Données partiellement masquées pour votre confidentialité
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
